import { Elements } from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";
import { LoadingSpinner } from "components/LoadingSpinner";
import { ISPAConfig } from "config/api";
import { ToastContext } from "context/ToastContext";
import { useFinixPaymentProvider } from "hooks/useFinixPaymentProvider";
import { PaymentSection } from "pages/OrderOverview/components/PaymentSection";
import { useEffect, useState, useContext } from "react";
import { useParams } from "react-router-dom";
import { useCreateOrderPaymentIntentMutation } from "store/queries/order";

import { FinixPaymentSection } from "../FinixPaymentSection/FinixPaymentSection";
import ErrorMessage from "../Overview/components/ErrorMessage";

const getPublishableKey = () => {
  const config = window?.config as ISPAConfig;

  if (!config) {
    throw new Error("Config not found");
  }

  return config.stripe.publishableKey;
};

interface IPayNowSectionProps {
  expirationDays?: number;
  orderInfo: any;
  showPaymentSection: any;
  setShowPaymentSection: any;
  clearPrice: number;
  totalSaving: number;
  fullPrice: number;
  payRemainingPrice: boolean;
  onReturnClick?: () => void;
  onUpdateOrder?: () => void;
  orderExternalId?: string;
}

function StripePayNowSection({
  expirationDays,
  orderInfo,
  showPaymentSection,
  setShowPaymentSection,
  clearPrice,
  totalSaving,
  fullPrice,
  payRemainingPrice,
  onReturnClick,
  onUpdateOrder,
  orderExternalId,
}: IPayNowSectionProps) {
  const { id: externalId } = useParams();
  const { current: toastElement } = useContext(ToastContext);

  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);

  useEffect(() => {
    setStripePromise(loadStripe(getPublishableKey()));
  }, []);

  const [createOrderPaymentIntent, { isLoading: isStripeLoading }] =
    useCreateOrderPaymentIntentMutation();

  const [clientSecret, setClientSecret] = useState("");
  const [paymentInitError, setPaymentInitError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  useEffect(() => {
    createOrderPaymentIntent({
      orderId: externalId || orderExternalId || "",
      paymentPlan: false,
      payRemainingPrice,
    })
      .unwrap()
      .then(({ data }: { data: any }) => {
        setClientSecret(data.client_secret);
        setPaymentInitError(null);
      })
      .catch((error) => {
        const errorMessage =
          error?.data?.messages?.[0] ||
          "There was an issue setting up your payment. Please try again.";
        setPaymentInitError(errorMessage);

        toastElement?.show({
          severity: "error",
          summary: "Payment Setup Error",
          detail: errorMessage,
        });
      });
  }, [externalId, payRemainingPrice, isRetrying]);

  const handleRetry = () => {
    setIsRetrying(!isRetrying); // Toggle to trigger useEffect
  };

  // Show error UI if payment intent creation failed
  if (paymentInitError) {
    return (
      <ErrorMessage
        paymentInitError={paymentInitError}
        onTryAgain={handleRetry}
        buttonDisabled={isStripeLoading}
      />
    );
  }

  if (!clientSecret?.length) {
    return <LoadingSpinner />;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          rules: {
            ".Label": {
              color: "#64636E",
              fontWeight: "500",
              fontSize: "14px",
              lineHeight: "20px",
            },
            ".Input": {
              borderRadius: "8px",
              border: "1px solid #D2D6DB",
              boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
              marginBottom: "12px",
            },
          },
        },
      }}
    >
      <PaymentSection
        expirationDays={expirationDays || 0}
        order={orderInfo}
        prices={{ clearPrice, totalSaving, fullPrice }}
        onReturnClick={onReturnClick}
        showPaymentSection={showPaymentSection}
        setShowPaymentSection={setShowPaymentSection}
        payRemainingPrice={payRemainingPrice}
        createOrderPaymentIntent={createOrderPaymentIntent}
        onUpdateOrder={onUpdateOrder}
        isDownPaymentOnly={false}
      />
    </Elements>
  );
}

export function PayNowSection(props: IPayNowSectionProps) {
  const {
    orderInfo,
    showPaymentSection,
    setShowPaymentSection,
    clearPrice,
    totalSaving,
    fullPrice,
    onReturnClick,
    onUpdateOrder,
  } = props;

  const { isFinix, isLoading, isError, finixApplicationId, finixEnvironment } =
    useFinixPaymentProvider(orderInfo);

  // Finix payment flow — no Stripe Elements, no client_secret
  if (isFinix) {
    if (isLoading) {
      return <LoadingSpinner />;
    }

    if (isError || !finixApplicationId) {
      return (
        <ErrorMessage
          paymentInitError="Unable to load payment provider. Please refresh and try again."
          onTryAgain={() => window.location.reload()}
          buttonDisabled={false}
        />
      );
    }

    return (
      <FinixPaymentSection
        order={orderInfo}
        prices={{ clearPrice, totalSaving, fullPrice }}
        finixApplicationId={finixApplicationId}
        finixEnvironment={finixEnvironment}
        showPaymentSection={showPaymentSection}
        setShowPaymentSection={setShowPaymentSection}
        onReturnClick={onReturnClick}
        onUpdateOrder={onUpdateOrder}
      />
    );
  }

  // Stripe payment flow (unchanged)
  return <StripePayNowSection {...props} />;
}
